import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { api, type ApiError } from '@/lib/api';
import { toast } from '@/lib/toast';
import { useCompletionNote } from './CompletionNote';
import type { Client, Project, Task, TaskStatus } from './types';

export const taskKeys = {
  all: ['tasks'] as const,
  list: (filters: TaskFilters) => ['tasks', 'list', filters] as const,
};

export interface TaskFilters {
  client_id?: number;
  project_id?: number;
  /** Direct children of this task only. */
  parent_task_id?: number;
  max_estimate_minutes?: number;
  include_closed?: boolean;
  overdue_only?: boolean;
  /** When set, only these statuses are returned (overrides include_closed). */
  status_in?: TaskStatus[];
  completed_after?: string;
  completed_before?: string;
  /** Server ordering; use `completed_at_desc` for history views. */
  sort?: 'default' | 'completed_at_desc';
  /** Done in the backlog or finished on the plan (includes recurring reopens). */
  completed_history?: boolean;
}

/** Drop undefined entries so `{ client_id: undefined }` and `{}` share one cache key. */
function normalizeFilters(filters: TaskFilters): TaskFilters {
  const next: TaskFilters = {};
  if (filters.client_id !== undefined) next.client_id = filters.client_id;
  if (filters.project_id !== undefined) next.project_id = filters.project_id;
  if (filters.parent_task_id !== undefined) next.parent_task_id = filters.parent_task_id;
  if (filters.max_estimate_minutes !== undefined) {
    next.max_estimate_minutes = filters.max_estimate_minutes;
  }
  if (filters.include_closed !== undefined) next.include_closed = filters.include_closed;
  if (filters.overdue_only !== undefined) next.overdue_only = filters.overdue_only;
  if (filters.status_in !== undefined && filters.status_in.length > 0) {
    next.status_in = [...filters.status_in].sort();
  }
  if (filters.completed_after !== undefined) next.completed_after = filters.completed_after;
  if (filters.completed_before !== undefined) next.completed_before = filters.completed_before;
  if (filters.sort !== undefined) next.sort = filters.sort;
  if (filters.completed_history !== undefined) next.completed_history = filters.completed_history;
  return next;
}

function toQuery(filters: TaskFilters): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value === undefined) continue;
    if (key === 'status_in' && Array.isArray(value)) {
      for (const status of value) params.append('status_in', status);
      continue;
    }
    params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export function useClients() {
  return useQuery<Client[]>({
    queryKey: ['clients'],
    queryFn: () => api<Client[]>('/api/clients'),
  });
}

export function useProjects(clientId?: number) {
  return useQuery<Project[]>({
    queryKey: ['projects', clientId ?? null],
    queryFn: () =>
      api<Project[]>(`/api/projects${clientId ? `?client_id=${clientId}` : ''}`),
  });
}

export function useTasks(filters: TaskFilters = {}) {
  const normalized = normalizeFilters(filters);
  return useQuery<Task[]>({
    queryKey: taskKeys.list(normalized),
    queryFn: () => api<Task[]>(`/api/tasks${toQuery(normalized)}`),
    // Filter chips swap the key on every click. Without this the list blanks
    // to a skeleton (or an error from a blipped refetch) and the backlog feels
    // broken when returning to "All clients".
    placeholderData: keepPreviousData,
  });
}

/**
 * One task by id.
 *
 * Backs anything that has to show a task the current list does not hold, a
 * done or deferred subtask opened from its parent, or a parent opened from a
 * child. Keyed under the `tasks` prefix so ordinary invalidation refreshes it.
 */
export function useTask(id: number | null | undefined) {
  return useQuery<Task>({
    queryKey: [...taskKeys.all, 'one', id],
    queryFn: () => api<Task>(`/api/tasks/${id}`),
    enabled: id != null,
  });
}

export function useUpdateTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: { id: number } & Partial<Task>) =>
      api<Task>(`/api/tasks/${id}`, { method: 'PATCH', json: patch }),
    // Rollups change on every ancestor, so refetching the list is both simpler
    // and more correct than patching the cache by hand. Status changes can
    // clear Plan time blocks, so invalidate those too. Completing also stops
    // the server-side timer, so the running chip has to be re-read or it keeps
    // counting time nobody is spending.
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: taskKeys.all });
      void qc.invalidateQueries({ queryKey: ['time-blocks'] });
      void qc.invalidateQueries({ queryKey: ['timer'] });
      void qc.invalidateQueries({ queryKey: ['reports', 'daily'] });
    },
  });
}

export function useToggleDone() {
  const update = useUpdateTask();
  const askForNote = useCompletionNote();

  return (task: Pick<Task, 'id' | 'status' | 'title'>) => {
    const next: TaskStatus = task.status === 'done' ? 'backlog' : 'done';
    update.mutate({ id: task.id, status: next });

    // Ask while the work is still in mind. Skipping is one tap, so a tick box
    // stays a tick box for anyone who has nothing to add.
    if (next === 'done') askForNote(task.id, task.title);

    // Completing removes the row from the default backlog view. Without a way
    // back, a mis-tap silently loses work and the sense of progress with it.
    if (next === 'done') {
      toast.undoable(`Completed "${task.title}"`, () =>
        update.mutate({ id: task.id, status: task.status }),
      );
    } else {
      toast.undoable(`Reopened "${task.title}"`, () =>
        update.mutate({ id: task.id, status: 'done' }),
      );
    }
  };
}

export interface CreateTaskInput {
  title: string;
  notes?: string | null;
  client_id?: number;
  project_id?: number | null;
  parent_task_id?: number | null;
  estimate_minutes?: number | null;
  priority?: Task['priority'];
  energy?: Task['energy'];
  due_at?: string | null;
}

export function useCreateTask() {
  const qc = useQueryClient();

  const create = useMutation<Task, ApiError, CreateTaskInput>({
    mutationFn: (input) => api<Task>('/api/tasks', { method: 'POST', json: input }),
    onSettled: () => qc.invalidateQueries({ queryKey: taskKeys.all }),
  });

  const remove = useMutation<void, ApiError, number>({
    mutationFn: (id) => api<void>(`/api/tasks/${id}`, { method: 'DELETE' }),
    onSettled: () => qc.invalidateQueries({ queryKey: taskKeys.all }),
  });

  return {
    mutate: create.mutate,
    isPending: create.isPending,
    /** Reverses a just-created task. Backs the undo toast. */
    undo: (id: number) => remove.mutate(id),
  };
}

export interface MoveTaskInput {
  id: number;
  /** `null` promotes the task to the top level. */
  parent_task_id: number | null;
  client_id?: number;
  project_id?: number | null;
}

/**
 * Re-parent a task and everything under it.
 *
 * The server rewrites the whole subtree's path, depth, and ownership in one
 * statement, and rejects a move beneath the task's own descendants with a 409,
 * so the client filters obvious nonsense out of the picker for clarity, but the
 * guarantee lives on the server.
 */
export function useMoveTask() {
  const qc = useQueryClient();
  return useMutation<Task, ApiError, MoveTaskInput>({
    mutationFn: ({ id, parent_task_id, client_id, project_id }) =>
      api<Task>(`/api/tasks/${id}/move`, {
        method: 'POST',
        json: {
          parent_task_id,
          ...(client_id !== undefined ? { client_id } : {}),
          ...(project_id !== undefined ? { project_id } : {}),
        },
      }),
    // A move changes client, project, and depth for every descendant, and
    // rollups for both the old and the new ancestors. Refetching is the only
    // honest option.
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: taskKeys.all });
      void qc.invalidateQueries({ queryKey: ['time-blocks'] });
    },
    onError: (error) => toast.error(error.message || 'Could not move task'),
  });
}

export function useDeleteTask() {
  const qc = useQueryClient();
  return useMutation<void, ApiError, number>({
    mutationFn: (id) => api<void>(`/api/tasks/${id}`, { method: 'DELETE' }),
    onSettled: () => qc.invalidateQueries({ queryKey: taskKeys.all }),
  });
}
